/**
 * FirmaEklemeTalebiCard.jsx — "Firma Ekleme Talebi" (firma tarafı)
 *
 * Standdaki bir firmanın, başka bir firmanın (dekoratör ya da taşeron: temizlik,
 * güvenlik, nakliye…) o standa eklenmesini istemesi. FirmaStandOverviewPage'in
 * "Stand Firmaları ve Taahütnameler" sekmesinde, salt-okunur Stant Firmaları
 * tablosunun altında yaşıyor — o tablonun "ekleme/kaldırma admin tarafına ait"
 * notunun cevabı bu kart.
 *
 * Kaynak: docs/guides/FE-stand-firma-atama-talebi-guide.md
 *
 * ⚠️ FİRMA SEÇİCİ YOK, BİLEREK. Kullanıcı firmanın adını SERBEST METİN yazar;
 * denetmen okuyup gerçek firmayı seçer ve onaylar (denetmen tarafı:
 * FirmaAtamaTalepPanel, Stand Düzenle popup'ında). Firma tablosu büyük ve
 * birbirine benzeyen legacy adlarla dolu — yanlış satırı seçmek sessizce yanlış
 * firmayı standa bağlar. Buraya `/firma/list` üzerinden bir ara-seç kutusu
 * EKLEMEYİN (Deniz'in kararı, 2026-08-29).
 *
 * ⚠️ Eşleme seviyesi (relation_level) hiçbir yerde SORULMAZ: sunucu kimin
 * istediğine bakarak türetir (katılımcı → 1, level-1 dekoratör → 2, …; taşeron
 * her zaman 1). İstemci gönderebilseydi bir dekoratör kendi taşeronunu standın
 * ana dekoratörü yapabilirdi.
 */
import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Table, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, FormGroup, Label, Input, FormText, Alert, Spinner,
} from "reactstrap";
import { Plus, Send, AlertTriangle, RefreshCw } from "lucide-react";
import {
  createFirmaAtamaTalebi, getKendiAtamaTalepleri,
  ATAMA_TALEP_DURUM, RELATION_LEVEL_LABELS, TALEP_TEXT_MAX,
} from "../../services/firmaAtamaTalepService";
import { getFirmaTypes } from "../../services/firmaAramaService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// Talep edilebilir roller: 3 (Dekoratör) ve >= 4 (taşeron ailesi). 1 Katılımcı /
// 2 Organizatör göndermek 400 — kimse onlara taşeronluk yapmaz.
// 15 (DenemeType) bir test satırı, rehber gizlenmesini söylüyor.
const HIDDEN_TYPE_IDS = [15];
function isRequestableType(t) {
  return (t.firmaTypeId === 3 || t.firmaTypeId >= 4) && !HIDDEN_TYPE_IDS.includes(t.firmaTypeId);
}

const EMPTY_FORM = {
  requestedFirmaTypeId: "3",
  firmaNameText: "",
  firmaContactNameText: "",
  firmaContactPhoneText: "",
  firmaContactEmailText: "",
};

export default function FirmaEklemeTalebiCard({ standId, fuarId, firmaId, forceLive = false }) {
  const { t: tx } = useTranslation("components");
  const [talepler, setTalepler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // KURAL: fetch mantığı Effect içinde tanımlanır (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!standId || !fuarId || !firmaId) return undefined;
    let ignore = false;

    async function run() {
      setLoading(true);
      setListError(null);
      try {
        const rows = await getKendiAtamaTalepleri({ firmaId, fuarId, standId }, { forceLive });
        if (!ignore) setTalepler(rows || []);
      } catch (e) {
        if (!ignore) setListError(e?.message || i18n.t("components:firmaEklemeTalebi.taleplerinizAlinamadi"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [standId, fuarId, firmaId, forceLive, reloadToken]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    let ignore = false;
    getFirmaTypes({ forceLive })
      .then((list) => { if (!ignore) setTypes((list || []).filter(isRequestableType)); })
      .catch(() => { if (!ignore) setTypes([]); });
    return () => { ignore = true; };
  }, [modalOpen, forceLive]);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const nameValid = form.firmaNameText.trim() !== "" && form.firmaNameText.trim().length <= TALEP_TEXT_MAX;

  async function handleSubmit() {
    if (!nameValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await createFirmaAtamaTalebi(
        { standId, fuarId, firmaId, ...form, requestedFirmaTypeId: Number(form.requestedFirmaTypeId) },
        { forceLive }
      );
      setModalOpen(false);
      setForm(EMPTY_FORM);
      setReloadToken((t) => t + 1);
      // ⚠️ `dekoratoreBaglanacak` (2026-09-03) SAKLANMAYAN bir alan — yalnızca bu POST
      // yanıtında var, listelerde her satırda `false` okunur. Bu yüzden mesajı burada,
      // talebin hemen ardından veriyoruz: talep sahibi standa bir firma eklediğini
      // sanıyor, oysa taşeron dekoratöre ve fuara bağlanacak ve standda hiç görünmeyecek.
      // Çapa onay anında canlı zincirden yeniden hesaplandığı için bu bir TAHMİN.
      setNotice(
        created?.dekoratoreBaglanacak
          ? tx("firmaEklemeTalebi.talebinizAlindiBuBirTaseron") +
            tx("firmaEklemeTalebi.onaylandigindaFirmaStandaDegilFirmaniza") +
            tx("firmaEklemeTalebi.kurdugunuzHerStanddaGecerliOlacak") +
            tx("firmaEklemeTalebi.gorunmeyecekTaseronlarSayfanizdanTakipEdin")
          : created?.maksimumDerinlikte
            ? tx("firmaEklemeTalebi.talebinizAlindiZincirEnDerin")
            : tx("firmaEklemeTalebi.talebinizAlindiDenetmenFirmayiEslestirdiginde")
      );
    } catch (e) {
      setError(e?.message || tx("firmaEklemeTalebi.talepGonderilemedi"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6 className="mb-0 fw-semibold">{tx("firmaEklemeTalebi.firmaEklemeTalepleri")}</h6>
            <div className="text-muted small">
              {tx("firmaEklemeTalebi.standinizaDekoratorYaDaTaseron")}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button
              color="secondary" outline size="sm" title={tx("common:yenile")}
              onClick={() => setReloadToken((t) => t + 1)} disabled={loading}
            >
              <RefreshCw size={13} />
            </Button>
            <Button color="success" size="sm" onClick={() => { setNotice(null); setModalOpen(true); }}>
              <Plus size={14} className="me-1" />{tx("firmaEklemeTalebi.firmaEklemeTalebi")}
            </Button>
          </div>
        </div>

        {notice && <Alert color="info" className="py-2 small">{notice}</Alert>}
        {listError && <Alert color="danger" className="py-2 small">{listError}</Alert>}

        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" /></div>
        ) : (
          <Table bordered responsive className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{tx("firmaEklemeTalebi.talepEdilenFirma")}</th>
                <th>{tx("firmaEklemeTalebi.rol")}</th>
                <th>{tx("firmaEklemeTalebi.eslemeSeviyesi")}</th>
                <th>{tx("firmaEklemeTalebi.durum")}</th>
                <th>{tx("firmaEklemeTalebi.sonuc")}</th>
              </tr>
            </thead>
            <tbody>
              {talepler.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">{tx("firmaEklemeTalebi.henuzTalepYok")}</td>
                </tr>
              ) : talepler.map((t) => {
                const durum = ATAMA_TALEP_DURUM[Number(t.isProcessed)] || ATAMA_TALEP_DURUM[0];
                return (
                  <tr key={t.id}>
                    <td>
                      <div className="fw-semibold">{t.firmaNameText}</div>
                      {(t.firmaContactNameText || t.firmaContactPhoneText) && (
                        <div className="text-muted small">
                          {[t.firmaContactNameText, t.firmaContactPhoneText].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td>{t.requestedFirmaTypeAdi || tx("firmaEklemeTalebi.tip", { requestedFirmaTypeId: t.requestedFirmaTypeId })}</td>
                    {/* Seviye sunucuda türetildi — salt okunur bilgi.
                        ⚠️ Talep bir taşeron içinse (>=4) ve dekoratör olarak açtıysanız
                        onay `firma_taseron`'a düşebilir; orada derinlik kavramı YOK, bu
                        yüzden seviye sayısı yanıltıcı olur. `dekoratoreBaglanacak`
                        listede saklanmadığı için rolden hareketle uyarıyoruz. */}
                    <td className="text-muted small">
                      {Number(t.requestedFirmaTypeId) >= 4
                        ? <span title={tx("firmaEklemeTalebi.taseronTalebiOnayDekoratorFuar")}>—</span>
                        : (RELATION_LEVEL_LABELS[t.relationLevel] || (t.relationLevel ?? "—"))}
                    </td>
                    <td><Badge color={durum.color} pill>{durum.label}</Badge></td>
                    <td className="small">
                      {Number(t.isProcessed) === 1 && (t.atananFirmaAdi || tx("firmaEklemeTalebi.firma", { atananFirmaId: t.atananFirmaId }))}
                      {Number(t.isProcessed) === 2 && <span className="text-danger">{t.redNedeni || "—"}</span>}
                      {Number(t.isProcessed) === 0 && <span className="text-muted">{tx("firmaEklemeTalebi.denetmenOnayiBekliyor")}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </CardBody>

      <Modal isOpen={modalOpen} toggle={submitting ? undefined : () => setModalOpen(false)} centered>
        <ModalHeader toggle={submitting ? undefined : () => setModalOpen(false)}>
          {tx("firmaEklemeTalebi.firmaEklemeTalebi")}
        </ModalHeader>
        <ModalBody>
          {error && <Alert color="danger" className="py-2">{error}</Alert>}

          <Row className="g-3">
            <Col md={12}>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("firmaEklemeTalebi.rol")}{" "}<span className="text-danger">*</span></Label>
                <Input
                  type="select" bsSize="sm" value={form.requestedFirmaTypeId} disabled={submitting}
                  onChange={(e) => update("requestedFirmaTypeId", e.target.value)}
                >
                  {types.length === 0 && <option value="3">{tx("firmaEklemeTalebi.dekorator")}</option>}
                  {types.map((t) => (
                    <option key={t.firmaTypeId} value={t.firmaTypeId}>{t.label}</option>
                  ))}
                </Input>
                <FormText>
                  {tx("firmaEklemeTalebi.standinDekoratorunuYalnizcaStandinKatilimcisi")}
                </FormText>
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("firmaEklemeTalebi.firmaAdi")}{" "}<span className="text-danger">*</span></Label>
                <Input
                  bsSize="sm" value={form.firmaNameText} maxLength={TALEP_TEXT_MAX} disabled={submitting}
                  placeholder="MONSTAR TASARIM"
                  onChange={(e) => update("firmaNameText", e.target.value)}
                />
                <FormText>
                  {tx("firmaEklemeTalebi.firmaninAdiniYazmanizYeterliListeden")}
                </FormText>
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("firmaEklemeTalebi.ilgiliKisi")}</Label>
                <Input
                  bsSize="sm" value={form.firmaContactNameText} maxLength={TALEP_TEXT_MAX} disabled={submitting}
                  onChange={(e) => update("firmaContactNameText", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("firmaEklemeTalebi.telefon")}</Label>
                <Input
                  bsSize="sm" value={form.firmaContactPhoneText} maxLength={TALEP_TEXT_MAX} disabled={submitting}
                  placeholder="0532 000 00 00"
                  onChange={(e) => update("firmaContactPhoneText", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("firmaEklemeTalebi.ePosta")}</Label>
                <Input
                  bsSize="sm" value={form.firmaContactEmailText} maxLength={TALEP_TEXT_MAX} disabled={submitting}
                  placeholder={tx("firmaEklemeTalebi.infoFirmaCom")}
                  onChange={(e) => update("firmaContactEmailText", e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col md={12}>
              <div className="text-muted small">
                <AlertTriangle size={12} className="me-1" />
                {tx("firmaEklemeTalebi.talebinizDogrudanUygulanmazBirDenetmen")}
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModalOpen(false)} disabled={submitting}>
            {tx("firmaEklemeTalebi.vazgec")}
          </Button>
          <Button color="success" onClick={handleSubmit} disabled={!nameValid || submitting}>
            {submitting ? <Spinner size="sm" className="me-1" /> : <Send size={14} className="me-1" />}
            {tx("firmaEklemeTalebi.talebiGonder")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
